import {
  clearReservationToken,
  getClientFacingMessage,
  readReservationToken,
  useGetMyReservationQuery,
  useGetPublicReservationQuery,
  useGrantReservationAccessMutation,
} from '@massilia/queries'
import { Button, Name } from '@massilia/ui'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, Check, Clock, Mail, Phone } from 'lucide-react'
import { useEffect, useState } from 'react'

import { PersonalDataAccessForm } from '@/components/access/personal-data-access-form'
import { ImportantNotice } from '@/components/ui/important-notice'
import { AGENCY_EMAIL, AGENCY_PHONE } from '@/lib/constants/agency'
import { useAuthStore } from '@/stores/auth.store'
import { formatDateShort } from '@/utils/format-date'

import { LoadingSkeleton } from '../reservation/-components/loading-skeleton'
import { ReservationError } from '../reservation/-components/reservation-error'

export const Route = createFileRoute('/_blue_header/confirmation-reservation/$reservationId')({
  component: ReservationConfirmationPage,
  errorComponent: ReservationError,
})

const ACCESS_ERROR = 'Demande de réservation introuvable ou nom incorrect.'

/**
 * Une bande de la page. Un seul niveau de boîte : les sections se suivent dans
 * un même cadre, séparées par un filet — deux cartes imbriquées sur un écran de
 * téléphone ne laissaient plus que quelques caractères par ligne.
 */
export function Band({ children, title }: { children: React.ReactNode; title: string }) {
  return (
    <section className="flex flex-col gap-3 border-t border-gray-light-200 p-4 first:border-t-0 md:p-6">
      <h2 className="text-sm font-semibold text-gray-light-900">{title}</h2>
      {children}
    </section>
  )
}

export function OptionItem({ label, selected }: { label: string; selected: boolean }) {
  return (
    <div className="flex items-start gap-2 text-sm text-gray-light-700">
      {selected ? (
        <Check aria-hidden className="mt-0.5 size-4 shrink-0 text-green-600" />
      ) : (
        <span aria-hidden className="text-gray-light-400 mt-0.5 w-4 shrink-0 text-center">
          –
        </span>
      )}
      <span className={selected ? '' : 'text-gray-light-500'}>
        {label} {selected ? '' : '(non retenue)'}
      </span>
    </div>
  )
}

/**
 * Confirmation d'une demande de réservation.
 *
 * C'est la page la plus sensible de l'espace public : identité, date de
 * naissance et numéro de pièce d'identité de chaque voyageur. Elle était servie
 * par une route sans garde — l'URL valait porteur, sans expiration ni
 * révocation. Trois preuves l'ouvrent désormais, dans cet ordre (ACCES-PERSO) :
 *
 * 1. le jeton remis à qui vient de réserver — cas nominal, aucune friction ;
 * 2. la session, si le dossier appartient au compte connecté ;
 * 3. à défaut, le nom de famille du client ou d'un voyageur.
 */
export function ReservationConfirmationPage() {
  const { reservationId } = Route.useParams()
  const { isAuthenticated } = useAuthStore()

  const [hasToken, setHasToken] = useState(() => readReservationToken(reservationId) !== null)
  const [accessError, setAccessError] = useState<null | string>(null)

  const grantAccess = useGrantReservationAccessMutation()

  const publicQuery = useGetPublicReservationQuery(reservationId, hasToken)
  // Un jeton périmé ne vaut plus rien : la suite se comporte comme sans jeton.
  // Déduit du rendu plutôt que recopié dans l'état par un effet, qui ferait un
  // rendu en cascade à chaque expiration.
  const tokenExpired = Boolean(publicQuery.error)

  // Sans jeton, un client connecté est servi par sa session : lui redemander son
  // propre nom serait absurde. S'il n'est pas propriétaire, la route répond 404
  // et on retombe sur le mur, comme un visiteur anonyme.
  const mineQuery = useGetMyReservationQuery(
    reservationId,
    (!hasToken || tokenExpired) && isAuthenticated
  )

  // Le jeton périmé est purgé du stockage : sans état à toucher ici, l'effet ne
  // porte que cet effet de bord.
  useEffect(() => {
    if (!publicQuery.error) return

    clearReservationToken(reservationId)
  }, [publicQuery.error, reservationId])

  const reservation = publicQuery.data ?? mineQuery.data

  if (!reservation) {
    if (publicQuery.isLoading || mineQuery.isLoading) {
      return <LoadingSkeleton />
    }

    return (
      <PersonalDataAccessForm
        description="Pour la retrouver, indiquez le nom de famille du client ou d’un voyageur."
        errorMessage={tokenExpired ? ACCESS_ERROR : accessError}
        isSubmitting={grantAccess.isPending}
        onSubmit={(credentials) =>
          grantAccess.mutate(
            { ...credentials, reservationId },
            {
              // Le message du serveur, quand il en a rédigé un : un calcul raté
              // se dit, il ne se confond pas avec un nom inconnu.
              onError: (failure) => setAccessError(getClientFacingMessage(failure, ACCESS_ERROR)),
              onSuccess: () => {
                setAccessError(null)
                setHasToken(true)
              },
            }
          )
        }
        submitLabel="Voir ma demande"
        title="Votre demande de réservation"
      />
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
      <header className="flex flex-col items-center gap-3 text-center">
        <div className="flex size-14 items-center justify-center rounded-full bg-green-100">
          <Check aria-hidden className="size-7 text-green-600" />
        </div>
        <h1 className="text-2xl font-semibold text-gray-light-900">Demande de réservation reçue</h1>
      </header>

      {/* Même vocabulaire que l'encart d'état d'un devis : où en est le dossier,
          et qui joue le coup suivant. */}
      <ImportantNotice icon={Clock} title="Prochaines étapes" variant="info">
        <p>Votre demande est arrivée avec les informations des voyageurs.</p>
        <p className="mt-1">
          Notre équipe la traite et vous recontacte pour finaliser votre réservation — paiement
          compris. Rien à faire de votre côté.
        </p>
      </ImportantNotice>

      <div className="overflow-hidden rounded-2xl border border-gray-light-200">
        <Band title="Coordonnées de contact">
          <dl className="flex flex-col gap-2">
            <Row label="Adresse e-mail">{reservation.client.email ?? 'Non renseignée'}</Row>
            {reservation.otherEmailAddress && (
              <Row label="Adresse secondaire">{reservation.otherEmailAddress}</Row>
            )}
            <Row label="Téléphone">{reservation.client.phoneNumber}</Row>
            {reservation.otherContactPhone && (
              <Row label="Téléphone secondaire">{reservation.otherContactPhone}</Row>
            )}
          </dl>
        </Band>

        <Band title={`Voyageurs (${reservation.passengers.length})`}>
          <dl className="flex flex-col gap-2">
            {reservation.passengers.map((passenger) => (
              // Jamais tronqué : c'est le nom qui doit correspondre au passeport,
              // c'est précisément ce que le client vient vérifier ici.
              <Row key={passenger.id} label={formatDateShort(passenger.birthDate)}>
                <Name>
                  {passenger.firstName} {passenger.lastName}
                </Name>
              </Row>
            ))}
          </dl>
        </Band>

        <Band title="Options sélectionnées">
          <div className="flex flex-col gap-2">
            <OptionItem label="Assurance annulation" selected={reservation.wantsInsurance} />
            <OptionItem label="Prêt de voiture" selected={reservation.wantsCarLoan} />
            <OptionItem label="Facture demandée" selected={reservation.wantsInvoice} />
          </div>
        </Band>

        <Band title="Besoin d’aide ?">
          <dl className="flex flex-col gap-2">
            <Row label="Téléphone">
              <a className="inline-flex items-center gap-2" href={`tel:${AGENCY_PHONE}`}>
                <Phone aria-hidden className="size-4 shrink-0" />
                {AGENCY_PHONE}
              </a>
            </Row>
            <Row label="Adresse e-mail">
              <a
                className="inline-flex items-center gap-2 break-all"
                href={`mailto:${AGENCY_EMAIL}`}
              >
                <Mail aria-hidden className="size-4 shrink-0" />
                {AGENCY_EMAIL}
              </a>
            </Row>
          </dl>
        </Band>
      </div>

      <Button asChild className="w-full sm:w-auto sm:self-start" variant="outline">
        <Link to="/">
          <ArrowLeft aria-hidden className="size-4" />
          Retour à l’accueil
        </Link>
      </Button>
    </div>
  )
}

/**
 * Une ligne « intitulé / valeur ». En colonne sur téléphone, en deux colonnes
 * dès qu'il y a la place : une adresse e-mail longue ne se tronque jamais, elle
 * revient à la ligne.
 */
export function Row({ children, label }: { children: React.ReactNode; label: string }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-4">
      <dt className="text-sm text-gray-light-600 sm:w-44 sm:shrink-0">{label}</dt>
      <dd className="min-w-0 text-sm break-words text-gray-light-900">{children}</dd>
    </div>
  )
}
